import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Questionnaire } from '../../../components/Questionnaire';
import OnboardingSidebar from './OnboardingSidebar';
import { OnboardingService } from '../../../Service/OnboardingService';
import { z } from 'zod';
import { QuestionType } from '../../../Utilis/Types/Task';
import {
  QuestionnaireQuestion,
  QuestionnaireSubmitValues,
} from '../../../components/Questionnaire/types';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setOnboardingData } from '../../../store/onboarding/onboarding-slice';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { Loading } from '../../../components/Loading';

export interface IFormInput {
  name: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  email: string;
  phone: string;
  terms: boolean;
}

const phoneNumberUtil = PhoneNumberUtil.getInstance();

const Onboarding = () => {
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const initialData = useAppSelector(state => state.onboarding);
  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState<QuestionnaireQuestion[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState<string>('');

  const stepIndex = useMemo(() => {
    return steps.findIndex(step => step.id === currentStep) || 0;
  }, [steps, currentStep]);

  useEffect(() => {
    getOnboardingData();
  }, []);

  const getOnboardingData = async () => {
    setIsLoading(true);
    try {
      const apis: any = [null, OnboardingService.getSteps()];

      if (!initialData.questions) {
        apis[0] = OnboardingService.getQuestions();
      }

      const [questionResult, stepsResult] = await Promise.all(apis);

      let questions;

      if (!!initialData.questions) {
        questions = initialData.questions;
      } else {
        questions = questionResult.data.map((question: any) => {
          const subQuestions = [];

          if (!!question.questions) {
            const sub = question.questions?.map((subQuestion: any) => {
              const name =
                subQuestion.referenced_table === 'users'
                  ? subQuestion.referenced_column
                  : subQuestion.id;

              return {
                ...subQuestion,
                name,
              };
            });

            subQuestions.push(...sub);
          }

          return {
            ...question,
            name: question.id,
            subQuestions,
          };
        });
      }

      setQuestions(questions);
      setSteps(stepsResult.data);
      setCurrentStep(questions[0].stepId);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const initialSchema = useMemo(() => {
    if (!questions.length) return null;

    if (!!initialData.schema) {
      return initialData.schema;
    }

    let validationObject: any = {};

    const allQuestions: any = [...questions];

    for (const question of questions) {
      if (!!question.subQuestions?.length) {
        allQuestions.push(...question.subQuestions!);
      }
    }

    for (const question of allQuestions) {
      let validation;

      switch (question.type) {
        case QuestionType.TEXTAREA:
        case QuestionType.INPUT:
          validation = z.string().refine(data => data.length >= 1, {
            message: 'Provide your answer before proceeding.',
          });
          break;
        case QuestionType.MULTISELECT:
          validation = z
            .array(z.string(), {
              required_error: 'Select at least one option before proceeding.',
            })
            .refine(arr => arr.length >= 1, {
              message: 'Select at least one option before proceeding.',
            });
          break;
        case QuestionType.SELECT:
          validation = z.string().refine(data => data.length >= 1, {
            message: 'Select one option before proceeding.',
          });
          break;
        case QuestionType.BOOLEAN:
          validation = z.boolean({
            required_error: 'Select one of the options',
          });
          break;
        case QuestionType.NUMBER:
          validation = z.number().min(1);
          break;
        case QuestionType.PHONE_NUMBER:
          validation = z
            .string({ required_error: 'Phone number is required' })
            .refine(value => {
              try {
                const parsedNumber = phoneNumberUtil.parse(value);
                const isValidNumber =
                  !!phoneNumberUtil.isValidNumber(parsedNumber);
                return isValidNumber;
              } catch {
                return false;
              }
            }, 'Invalid phone number');
          break;
        case QuestionType.EMAIL:
          validation = z
            .string({ required_error: 'Email is required' })
            .refine(
              value => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value),
              'Invalid email address'
            );
          break;
        default:
          validation = undefined;
          break;
      }

      if (!!validation) {
        validationObject[question.name] = validation;
      }
    }

    return z.object(validationObject);
  }, [questions]);

  const submitOnboarding = ({
    questions,
    schema,
    values,
  }: QuestionnaireSubmitValues) => {
    const answers = Object.entries(values);

    appDispatch(setOnboardingData({ questions, schema, answers: values }));

    const submittedAnswers = [];

    for (const [key, value] of answers) {
      const restricted = ['name', 'lastName', 'email', 'phone'];

      if (!restricted.includes(key)) {
        const answer: any = { questionId: key, answer: value };

        submittedAnswers.push(answer);
      }
    }

    const registerData = {
      info: {
        name: values.name,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
      },
      answers: submittedAnswers,
    };

    navigate('/register', { state: registerData });
  };

  return (
    <Loading isLoading={isLoading}>
      <Box sx={{ display: 'flex' }}>
        <OnboardingSidebar
          steps={steps.map(step => step.name)}
          currentStep={stepIndex}
        />
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center',
            '@media (max-width: 768px)': {
              alignItems: 'unset',
            },
          }}
        >
          <Questionnaire
            initialData={initialData.answers}
            questions={questions}
            validationSchema={initialSchema!}
            onChangeStep={setCurrentStep}
            onQuestionnaireFinish={submitOnboarding}
            onQuestionnaireQuit={() => navigate('introduction')}
          />
        </Box>
      </Box>
    </Loading>
  );
};

export default Onboarding;
